/**
 * Custom deferred media component that preloads videos when coming into view,
 * autoplays them and avoid stopping all videos when loading. Based on 
 * the original DeferredMedia component.
 */
if (!customElements.get('custom-deferred-media')) {
  class CustomDeferredMedia extends HTMLElement {
    constructor() {
      super();
      const poster = this.querySelector('[id^="Deferred-Poster-"]');
      if (!poster) return;
      poster.addEventListener('click', this.loadContent.bind(this));
    }

    /**
     * Customization: load content when coming into view
     */
    connectedCallback() {
      const options = { root: null, rootMargin: '0px', threshold: 0.1};

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) this.loadContent();
        });
      }, options);

      this.observer.observe(this);
    }

    /**
     * Customization: disconnect observer
     */
    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    loadContent(focus = true) {
      // Customization: Do not pause other media when loading
      // window.pauseAllMedia();

      if (!this.getAttribute('loaded')) {
        const content = document.createElement('div');
        content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

        this.setAttribute('loaded', true);
        const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
        if (focus) deferredElement.focus();

        // Customization: Autoplay videos after loading (Safari will not respect autoplay attribute)
        const video = this.querySelector('video');
        if (video) video.play();
      } else {
        // Play video if paues (as media gallery stops all videos on thumbnail click)
        const video = this.querySelector('video');
        if (video && video.paused) video.play();
      }
    }
  }

  customElements.define('custom-deferred-media', CustomDeferredMedia);
}

/**
 * Custom collapsible text component that shows/hides content with a toggle button.
 * By default only active on mobile devices. When the data-always-on attribute is
 * present, the component initializes on all screen sizes.
 */
if (!customElements.get('custom-collapsible-text')) {
  class CustomCollapsibleText extends HTMLElement {
    constructor() {
      super();
      // Support both generic (.collapsible-content) and legacy collection-description class names
      this.content = this.querySelector('.collapsible-content, .collection-description__content');
      this.toggle = this.querySelector('.collapsible-toggle, .collection-description__toggle');
      this.initialized = false;
      this.onToggleClickBound = this.onToggleClick.bind(this);
      this.onBreakpointChangeBound = this.onBreakpointChange.bind(this);
      
      if (!this.content || !this.toggle) return;

      // data-always-on: initialize on all screen sizes (e.g. product description)
      if (this.hasAttribute('data-always-on')) {
        this.init();
        return;
      }
      
      // Default: only initialize on mobile
      this.mobileMediaQuery = window.matchMedia('(max-width: 576px)');
      if (this.mobileMediaQuery.addEventListener) {
        this.mobileMediaQuery.addEventListener('change', this.onBreakpointChangeBound);
      } else {
        this.mobileMediaQuery.addListener(this.onBreakpointChangeBound);
      }
      
      if (this.mobileMediaQuery.matches) {
        this.init();
      }
    }

    init() {
      if (this.initialized) return;

      if (this.hasAttribute('data-always-on')) {
        const collapsedHeight = Number(this.dataset.collapsedHeight) || 350;
        const shouldCollapse = this.content.scrollHeight > collapsedHeight + 1;

        this.toggle.hidden = !shouldCollapse;
        this.classList.toggle('is-collapsed', shouldCollapse);

        if (!shouldCollapse) return;
      } else {
        this.classList.add('is-collapsed');
      }

      this.toggle.addEventListener('click', this.onToggleClickBound);
      this.initialized = true;
    }

    onBreakpointChange(event) {
      if (event.matches) {
        this.init();
      } else {
        this.destroy();
      }
    }

    onToggleClick() {
      // Capture expanded state before toggling
      const isExpanded = !this.classList.contains('is-collapsed');
      this.classList.toggle('is-collapsed');
      // On collapse, scroll back to the top of the block.
      // Only for data-always-on elements (product description), not collection description.
      if (isExpanded && this.hasAttribute('data-always-on')) {
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    destroy() {
      if (!this.initialized) return;
      this.toggle.removeEventListener('click', this.onToggleClickBound);
      this.classList.remove('is-collapsed');
      this.toggle.hidden = false;
      this.initialized = false;
    }
  }

  customElements.define('custom-collapsible-text', CustomCollapsibleText);
}


/**
 * Custom product finder line item component that adds the product finder line item attribute on product page 
 * for tracking of product finder purchases.
 */
if (!customElements.get('line-item-source')) {
  class LineItemSource extends HTMLElement {
    connectedCallback() {
      const supportedSources = new Set(['product_finder']);
      const params = new URLSearchParams(window.location.search);
      const sourceValue = params.get('source');
      if (!sourceValue || !supportedSources.has(sourceValue)) return;

      const input = this.querySelector('.line-item-source__input') || this.querySelector('input');
      if (!input) return;

      input.value = sourceValue;
      input.disabled = false;
    }
  }

  customElements.define('line-item-source', LineItemSource);
}

/**
 * Promo banner countdown timer
 * Displays a live countdown timer for promotional banners
 */
document.addEventListener('DOMContentLoaded', function() {
  const countdownTimers = document.querySelectorAll('.promo-banner__countdown-timer[data-countdown-end]');
  
  countdownTimers.forEach(function(timerEl) {
    const endDateStr = timerEl.getAttribute('data-countdown-end');
    const endedText = timerEl.getAttribute('data-countdown-ended') || 'Akce skončila';
    const daySingular = timerEl.getAttribute('data-day-singular') || 'den';
    const dayPluralFew = timerEl.getAttribute('data-day-plural-few') || 'dny';
    const dayPluralMany = timerEl.getAttribute('data-day-plural-many') || 'dní';
    
    const endDate = new Date(endDateStr).getTime();
    
    if (isNaN(endDate)) {
      timerEl.textContent = 'Neplatné datum';
      return;
    }

    function updateCountdown() {
      const now = new Date().getTime();
      const distance = endDate - now;

      if (distance < 0) {
        timerEl.textContent = endedText;
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      let timeStr = '';
      if (days > 0) {
        const dayLabel = days === 1 ? daySingular : (days >= 2 && days <= 4 ? dayPluralFew : dayPluralMany);
        timeStr = days + ' ' + dayLabel + ', ';
      }
      timeStr += String(hours).padStart(2, '0') + ':' +
                 String(minutes).padStart(2, '0') + ':' +
                 String(seconds).padStart(2, '0');

      timerEl.textContent = timeStr;
    }

    updateCountdown();
    setInterval(updateCountdown, 1000);
  });
});

/**
 * Product page: Other motives button state handling
 * Toggles arrow direction while modal is open.
 */
document.addEventListener('DOMContentLoaded', function() {
  const otherMotiveButtons = document.querySelectorAll('.other-motives__button[data-other-motives-modal-id]');
  const otherMotiveModals = document.querySelectorAll('.other-motives-modal');

  otherMotiveButtons.forEach(function(button) {
    button.addEventListener('click', function() {
      button.classList.add('is-open');
    });
  });

  otherMotiveModals.forEach(function(modal) {
    modal.addEventListener('click', function(event) {
      if (event.target === modal && typeof modal.hide === 'function') {
        modal.hide();
      }
    });
  });

  document.body.addEventListener('modalClosed', function() {
    otherMotiveButtons.forEach(function(button) {
      button.classList.remove('is-open');
    });
  });
});

/**
 * EU legal-guarantee notice modal.
 * Self-contained so the snippet can be reused outside the product page.
 */
if (!customElements.get('legal-guarantee-notice')) {
  class LegalGuaranteeNotice extends HTMLElement {
    constructor() {
      super();
      this.isOpen = false;
      this.onTriggerClick = this.onTriggerClick.bind(this);
      this.onModalClick = this.onModalClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
    }

    connectedCallback() {
      this.trigger = this.querySelector('[data-legal-guarantee-notice-open]');
      this.modal = this.querySelector('.legal-guarantee-notice__modal');
      if (!this.trigger || !this.modal) return;

      this.trigger.addEventListener('click', this.onTriggerClick);
      this.modal.addEventListener('click', this.onModalClick);
    }

    disconnectedCallback() {
      this.close();
      if (this.trigger) this.trigger.removeEventListener('click', this.onTriggerClick);
      if (this.modal) this.modal.removeEventListener('click', this.onModalClick);
    }

    onTriggerClick(event) {
      event.preventDefault();
      this.open();
    }

    onModalClick(event) {
      if (event.target.closest('[data-legal-guarantee-notice-close]')) {
        this.close();
      }
    }

    onKeyDown(event) {
      if (event.key === 'Escape') this.close();
    }

    open() {
      if (!this.modal || this.isOpen) return;

      this.modalParent = this.modal.parentNode;
      this.modalNext = this.modal.nextSibling;
      document.body.appendChild(this.modal);
      this.modal.classList.add('is-open');
      this.modal.removeAttribute('hidden');
      document.body.classList.add('legal-guarantee-notice-open');
      document.addEventListener('keydown', this.onKeyDown);
      this.isOpen = true;

      const closeButton = this.modal.querySelector('.legal-guarantee-notice__close');
      if (closeButton) closeButton.focus();
    }

    close() {
      if (!this.modal || !this.isOpen) return;

      this.modal.classList.remove('is-open');
      this.modal.setAttribute('hidden', '');
      document.body.classList.remove('legal-guarantee-notice-open');
      document.removeEventListener('keydown', this.onKeyDown);

      if (this.modalParent) {
        if (this.modalNext && this.modalNext.parentNode === this.modalParent) {
          this.modalParent.insertBefore(this.modal, this.modalNext);
        } else {
          this.modalParent.appendChild(this.modal);
        }
        this.modalParent = null;
        this.modalNext = null;
      }

      this.isOpen = false;
      if (this.trigger) this.trigger.focus();
    }
  }

  customElements.define('legal-guarantee-notice', LegalGuaranteeNotice);
}

/**
 * Password overlay for unlaunched markets (see snippets/market-lock.liquid).
 * The entered password is SHA-256 hashed and compared with the token rendered
 * from theme settings; on match a cookie lets the visitor through on later visits.
 */
if (!customElements.get('market-lock')) {
  class MarketLock extends HTMLElement {
    constructor() {
      super();
      this.inertElements = [];
      this.onSubmit = this.onSubmit.bind(this);
    }

    connectedCallback() {
      if (document.documentElement.classList.contains('market-lock-unlocked')) {
        this.remove();
        return;
      }

      this.form = this.querySelector('.market-lock__form');
      this.input = this.querySelector('.market-lock__input');
      this.error = this.querySelector('.market-lock__error');
      if (!this.form || !this.input) return;

      this.form.addEventListener('submit', this.onSubmit);
      this.lockPage();
      this.input.focus();
    }

    disconnectedCallback() {
      if (this.form) this.form.removeEventListener('submit', this.onSubmit);
      this.unlockPage();
    }

    async onSubmit(event) {
      event.preventDefault();

      const value = this.input.value.trim();
      const isValid = value !== '' && (await this.hash(value)) === this.dataset.token;

      if (!isValid) {
        if (this.error) this.error.hidden = false;
        this.input.setAttribute('aria-invalid', 'true');
        this.input.select();
        return;
      }

      document.cookie = `market_lock=${this.dataset.token}; path=/; max-age=${60 * 60 * 24 * 30}; SameSite=Lax; Secure`;
      document.documentElement.classList.add('market-lock-unlocked');
      this.remove();
    }

    async hash(value) {
      const buffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
      return Array.from(new Uint8Array(buffer), (byte) => byte.toString(16).padStart(2, '0')).join('');
    }

    lockPage() {
      document.documentElement.classList.add('market-lock-open');
      this.inertElements = Array.from(document.body.children).filter((element) => element !== this && !element.inert);
      this.inertElements.forEach((element) => { element.inert = true; });
    }

    unlockPage() {
      document.documentElement.classList.remove('market-lock-open');
      this.inertElements.forEach((element) => { element.inert = false; });
      this.inertElements = [];
    }
  }

  customElements.define('market-lock', MarketLock);
}